import { MessengerSettingsV2, MessengerV2 } from 'lib-shared-legacy-types';
import { useCallback, useEffect, useState } from 'react';

import { messengersApi } from 'apis/messengers';
import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';
import { FormValues } from 'components/settings/components/sections/dixaMessenger/views/settings/types';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { messengerSettingsActions } from '../messengerSettings.actions';
import { selectKnowledgeState } from '../messengerSettings.selectors';

import { useAddBoardIdIfNeeded } from './useAddBoardIdIfNeeded';
import { formatFormValuesForServerSide } from './utils';

interface UseAppearanceSettingsReturnType {
  isLoading: boolean;
  setSettings: (formValues: FormValues) => void;
  messenger: MessengerV2 | null;
  bulletinBoardId: string | null;
  fallbackSettings: MessengerSettingsV2 | undefined;
}

export function useMessengerSettings(
  messengerId?: string,
): UseAppearanceSettingsReturnType {
  const knowledgeState = useAppSelector(selectKnowledgeState);
  const orgId = useAppSelector(
    ({ settings }) => settings.organization.organization.id,
  );
  const navigate = useAppNavigate();
  const notify = useNotifications();
  const dispatch = useAppDispatch();
  const messengerApi = useDixaMessengerApi();
  const createBoardIfNeeded = useAddBoardIdIfNeeded(messengerApi, messengerId);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messenger, setMessenger] = useState<MessengerV2 | null>(null);

  const setMessengerCallback = useCallback(
    (messenger: MessengerV2) => {
      setMessenger(messenger);
      if (messenger.settings) {
        dispatch(messengerSettingsActions.initialize(messenger.settings));
      }
    },
    [dispatch, setMessenger],
  );

  const { data: fallbackSettings } =
    messengersApi.useReadGlobalFallbackSettingsQuery();

  const goBackToMessengerListPage = useCallback(
    () => navigate({ path: '/settings/dixa-messenger' }),
    [navigate],
  );

  const notifyError = useCallback(
    (message: string) => notify({ title: message, type: 'error' }),
    [notify],
  );

  useEffect(() => {
    if (!messengerId) {
      goBackToMessengerListPage();
      return;
    }

    setIsLoading(true);

    messengerApi
      .fetch(messengerId)
      .then(createBoardIfNeeded)
      .then(setMessengerCallback)
      .catch(() => {
        notifyError('Failed to load the settings');
        goBackToMessengerListPage();
      })
      .finally(() => setIsLoading(false));
  }, [
    messengerId,
    notifyError,
    goBackToMessengerListPage,
    createBoardIfNeeded,
    messengerApi,
    dispatch,
    setMessengerCallback,
  ]);

  const setSettings = useCallback(
    (values: FormValues) => {
      if (!messengerId) {
        notifyError(
          'Failed to save the settings due to unidentified messenger',
        );
        return;
      }

      setIsLoading(true);
      const formattedValues = formatFormValuesForServerSide(
        values,
        knowledgeState,
        { orgId },
      );

      messengerApi
        .patchSettings(messengerId, formattedValues)
        .then((messenger) => {
          notify({
            title: 'Settings updated successfully',
            type: 'success',
            time: 3000,
          });
          setMessengerCallback(messenger);
        })
        .catch(() => notifyError('Failed to save the settings'))
        .finally(() => setIsLoading(false));
    },
    [
      messengerId,
      notifyError,
      notify,
      messengerApi,
      knowledgeState,
      setMessengerCallback,
      orgId,
    ],
  );

  return {
    isLoading,
    setSettings,
    messenger,
    bulletinBoardId:
      messenger?.settings?.integrations.bulletinBoard?.boardId || null,
    fallbackSettings,
  };
}
